import { fromIntToMonetary, fromMonetaryToInt } from "@common/helpers/monetary";
import {
    ScheduleApplicationPeriodRole,
    ScheduleApplicationPeriodStatus,
    ScheduleJeSubtype,
} from "@common/types/domains/accounting/schedule";
import { AccruedRecognitionPeriod, toMonthKey } from "./accruedBillingRule";
import { isLiveLadderPeriod } from "./scheduleLadderPeriods";
import {
    ScheduleBalanceAdjustmentCandidate,
    isScheduleRecognitionAdjustmentEntry,
} from "./scheduleLifecycleEntries";

interface RecognitionPeriodInput {
    id: string;
    effectiveDate: string;
    amount: number;
    status: ScheduleApplicationPeriodStatus;
    subtype: ScheduleJeSubtype;
    role: ScheduleApplicationPeriodRole;
    reversedByPeriodId: string | null;
    revision: number;
    journalEntry: { revision: number } | null;
}

/** Signed from the holding account's point of view, as the list item carries it. */
type LifecycleEntryInput = ScheduleBalanceAdjustmentCandidate & {
    revision: number;
    amount: number;
    dateOnly: string | null;
};

export interface RecognizedLedgerEntry {
    dateOnly: string | null;
    amount: number;
}

/**
 * Everything the ledger has recognized for a schedule, entry by entry: the
 * live posted months plus the adjustments of a catch-up change or an
 * accelerated stop. A reversed month is read from its rows: the original
 * stays posted but is marked `reversedByPeriodId`, and the restatement is a
 * row of its own. A legacy reversal shares its original's row instead and
 * carries the revision of the entry it cancels, so a month restated that
 * way still counts its live amount. A reversed opening entry moved deferral
 * rather than recognition, so its reversal doesn't count.
 */
export function toRecognizedLedgerEntries(options: {
    periods: RecognitionPeriodInput[];
    lifecycleJournalEntries: LifecycleEntryInput[];
    /**
     * Ladder months dated on or after this day are left out, as a stop
     * there voids them. Adjustments count regardless of their date, and a
     * reversal row still finds the month it cancels among every period.
     */
    ladderCutoff?: string;
}): RecognizedLedgerEntry[] {
    const legacyReversedKeys = new Set(
        options.lifecycleJournalEntries.flatMap((entry) =>
            entry.subtype === ScheduleJeSubtype.reversal &&
            entry.periodId !== null &&
            entry.reversesPeriodId === null
                ? [`${entry.periodId}:${entry.revision}`]
                : [],
        ),
    );
    const postedRecognition = options.periods.flatMap((period) =>
        period.status === ScheduleApplicationPeriodStatus.posted &&
        period.subtype === ScheduleJeSubtype.recognized &&
        isLiveLadderPeriod(period) &&
        (options.ladderCutoff === undefined ||
            period.effectiveDate < options.ladderCutoff) &&
        !legacyReversedKeys.has(
            `${period.id}:${period.journalEntry?.revision ?? period.revision}`,
        )
            ? [{ dateOnly: period.effectiveDate, amount: period.amount }]
            : [],
    );
    const ladderPeriodIds = new Set(
        options.periods.flatMap((period) =>
            period.role === ScheduleApplicationPeriodRole.ladder
                ? [period.id]
                : [],
        ),
    );
    const unlinkedAdjustments = options.lifecycleJournalEntries.flatMap(
        (entry) =>
            isScheduleRecognitionAdjustmentEntry({ entry, ladderPeriodIds })
                ? [{ dateOnly: entry.dateOnly, amount: entry.amount }]
                : [],
    );
    return [...postedRecognition, ...unlinkedAdjustments];
}

/** The recognized entries the cumulative rule can place on a month-end. */
export function toAccruedRecognitionPeriods(options: {
    periods: RecognitionPeriodInput[];
    lifecycleJournalEntries: LifecycleEntryInput[];
}): AccruedRecognitionPeriod[] {
    return toRecognizedLedgerEntries(options).flatMap((entry) =>
        entry.dateOnly === null
            ? []
            : [{ effectiveDate: entry.dateOnly, amount: entry.amount }],
    );
}

/** Recognition dated on or before the end of `dateOnly`'s month. */
export function recognizedThrough(options: {
    periods: AccruedRecognitionPeriod[];
    dateOnly: string;
}): number {
    const month = toMonthKey(options.dateOnly);
    return fromIntToMonetary(
        options.periods.reduce(
            (sum, period) =>
                toMonthKey(period.effectiveDate) <= month
                    ? sum + fromMonetaryToInt(period.amount)
                    : sum,
            0,
        ),
    );
}

/**
 * What an accrued schedule still has to bill on `asOfDate`: everything
 * recognized through that month-end, less every billing so far, never
 * below zero.
 */
export function unbilledBalanceAsOf(options: {
    periods: AccruedRecognitionPeriod[];
    billedAmount: number;
    asOfDate: string;
}): number {
    const unbilledCents =
        fromMonetaryToInt(
            recognizedThrough({
                periods: options.periods,
                dateOnly: options.asOfDate,
            }),
        ) - fromMonetaryToInt(options.billedAmount);
    return fromIntToMonetary(Math.max(unbilledCents, 0));
}
